import { MouseEvent, useState } from 'react';

import {
  Button,
  IconButton,
  Modal,
  ModalActions,
  ModalContent,
  ModalWrapper,
  Typography,
  useDeviceType,
} from '@braskai/ui-kit';

import { Close } from '@mui/icons-material';
import { Box, Stack } from '@mui/material';

import { useIntroduceBoostersModalStore } from '@/features/boosters/model/useIntroduceBoostersModalStore';

import { useInterfaceLanguage } from '@/shared/i18n';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { BoosterType } from '@/shared/types/Billing';

import { getBoosterTooltipMap } from '../../lib/boosterTooltipData';
import { BOOSTERS_HELP_URL } from '../../lib/constants';
import { formatBoosterPrice } from '../../lib/formatBoosterPrice';
import { useBoosterConfirmation } from '../../model/useBoosterConfirmation';
import { useBoosters } from '../../model/useBoosters';
import { useBoosterSeatsCounter } from '../../model/useBoosterSeatsCounter';
import { BoosterItem } from '../BoosterItem/BoosterItem';
import { BoosterSeatsCounter } from '../BoosterSeatsCounter/BoosterSeatsCounter';
import BoostersBanner from './assets/boosters-banner.png';

const IntroduceBoostersModalComponent = () => {
  const onClose = useIntroduceBoostersModalStore((state) => state.close);
  const openIntroduceBoostersModal = useIntroduceBoostersModalStore((state) => state.open);
  const { allBoosters } = useBoosters();
  const { isMobile } = useDeviceType();
  const { locale, t } = useInterfaceLanguage();
  const boosterTooltipMap = getBoosterTooltipMap(t);

  const { decrement, increment, seatsCount } = useBoosterSeatsCounter(1);
  const { openBoosterConfirmation } = useBoosterConfirmation();

  const [selectedBoosters, setSelectedBoosters] = useState<Record<BoosterType, boolean>>({
    [BoosterType.SRT]: true,
    [BoosterType.TeamspaceSeat]: true,
    [BoosterType.TranslationPrompting]: true,
  });

  const toggleBooster = (id: BoosterType) => {
    setSelectedBoosters((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const incrementSeats = (e: MouseEvent) => {
    e.stopPropagation();
    increment();
  };

  const decrementSeats = (e: MouseEvent) => {
    e.stopPropagation();
    decrement();
  };

  const handleAddAndContinue = () => {
    MixpanelEvents.boostersContinueClick({
      boosters_prompting: selectedBoosters[BoosterType.TranslationPrompting],
      boosters_seats: selectedBoosters[BoosterType.TeamspaceSeat] ? seatsCount : 0,
      boosters_srt: selectedBoosters[BoosterType.SRT],
      source: 'boosters_general_modal',
    });
    onClose();

    openBoosterConfirmation({
      seatsCount,
      selectedBoosters,
      onCancel: openIntroduceBoostersModal,
    });
  };

  const isContinueDisabled = !Object.values(selectedBoosters).some(Boolean);

  return (
    <Modal size="xs" open onClose={onClose}>
      <ModalContent>
        <Stack alignItems="center" flexDirection="row" justifyContent="space-between" mb={16}>
          <Typography color="grey.900" variant={isMobile ? 'title-4-md' : 'title-3-md'}>
            {t('boosters.intro.title')}
          </Typography>
          <IconButton data-testid="introduce-boosters-modal-close" onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <Stack>
          {/* Banner Image */}
          <Box
            sx={{
              borderRadius: '12px',
              height: 'auto',
              mb: 20,
              objectFit: 'cover',
              width: '100%',
            }}
            alt=""
            component="img"
            src={BoostersBanner}
          />

          {/* Description */}
          <Stack gap={8} mb={8}>
            <Typography color="grey.900" variant="body-2">
              {t('boosters.manage.description')}
            </Typography>
            <Stack flexDirection="row" gap={4}>
              <Box
                component="a"
                href={BOOSTERS_HELP_URL}
                sx={{ cursor: 'pointer', textDecoration: 'none' }}
                target="_blank"
              >
                <Typography color="primary.700" component="span" variant="body-2-md">
                  {t('boosters.learnMore')}
                </Typography>
              </Box>
              <Typography color="grey.900" variant="body-2">
                {t('boosters.about')}
              </Typography>
            </Stack>
          </Stack>

          {/* Boosters List */}
          <Stack gap={8}>
            {allBoosters && (
              <>
                {allBoosters[BoosterType.SRT] && (
                  <BoosterItem
                    title={t('boosters.item.srt.title')}
                    price={t('boosters.perMonth', {
                      price: formatBoosterPrice(allBoosters, BoosterType.SRT, 1, locale),
                    })}
                    description={t('boosters.item.srt.description')}
                    isChecked={selectedBoosters[BoosterType.SRT]}
                    tooltipProps={boosterTooltipMap[BoosterType.SRT]}
                    onChange={() => toggleBooster(BoosterType.SRT)}
                  />
                )}

                {allBoosters[BoosterType.TranslationPrompting] && (
                  <BoosterItem
                    title={t('boosters.item.prompting.title')}
                    price={t('boosters.perMonth', {
                      price: formatBoosterPrice(allBoosters, BoosterType.TranslationPrompting, 1, locale),
                    })}
                    description={t('boosters.item.prompting.description')}
                    isChecked={selectedBoosters[BoosterType.TranslationPrompting]}
                    tooltipProps={boosterTooltipMap[BoosterType.TranslationPrompting]}
                    onChange={() => toggleBooster(BoosterType.TranslationPrompting)}
                  />
                )}

                {allBoosters[BoosterType.TeamspaceSeat] && (
                  <BoosterItem
                    title={t('boosters.item.seat.title')}
                    price={t('boosters.perMonth', {
                      price: formatBoosterPrice(allBoosters, BoosterType.TeamspaceSeat, seatsCount, locale),
                    })}
                    rightContent={
                      selectedBoosters[BoosterType.TeamspaceSeat] ? (
                        <BoosterSeatsCounter
                          seatsCount={seatsCount}
                          onDecrement={decrementSeats}
                          onIncrement={incrementSeats}
                        />
                      ) : null
                    }
                    description={t('boosters.item.seat.description')}
                    isChecked={selectedBoosters[BoosterType.TeamspaceSeat]}
                    tooltipProps={boosterTooltipMap[BoosterType.TeamspaceSeat]}
                    onChange={() => toggleBooster(BoosterType.TeamspaceSeat)}
                  />
                )}
              </>
            )}
          </Stack>
        </Stack>
      </ModalContent>
      <ModalActions>
        <Button
          data-testid="introduce-boosters-modal-add-and-continue"
          disabled={isContinueDisabled}
          size="lg"
          fullWidth
          onClick={handleAddAndContinue}
        >
          {t('boosters.action.addAndContinue')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export const IntroduceBoostersModal = () => (
  <ModalWrapper hook={useIntroduceBoostersModalStore}>
    <IntroduceBoostersModalComponent />
  </ModalWrapper>
);
